import React from "react"

// =============================================================================
// Geometry helpers
// =============================================================================

const RADIUS = 9
const CENTER = 12
const START_X = CENTER
const START_Y = CENTER - RADIUS

function pieSector(progress: number): string | null {
  if (progress <= 0) return null
  if (progress >= 1) return null

  const endAngle = -Math.PI / 2 + progress * 2 * Math.PI
  const endX = CENTER + RADIUS * Math.cos(endAngle)
  const endY = CENTER + RADIUS * Math.sin(endAngle)
  const largeArc = progress > 0.5 ? 1 : 0

  return `M ${CENTER} ${CENTER} L ${START_X} ${START_Y} A ${RADIUS} ${RADIUS} 0 ${largeArc} 1 ${endX} ${endY} Z`
}

// =============================================================================
// Props
// =============================================================================

export type ProgressCircleProps = {
  /** Value between 0 and 1. 0 = empty ring, 1 = fully filled. */
  progress: number
  /**
   * Accessible label describing what this progress represents (e.g. "Bulk export").
   * When provided, renders as `role="progressbar"` with full ARIA attributes.
   * When omitted, renders as `aria-hidden` — use this when a parent element
   * (e.g. a button with its own label) already provides the accessible context.
   */
  "aria-label"?: string
  /**
   * Human-readable progress text announced by screen readers
   * (e.g. "60% complete"). Defaults to "{n}% complete" when `aria-label` is set.
   */
  "aria-valuetext"?: string
}

// =============================================================================
// Component
// =============================================================================

/**
 * Circular SVG progress indicator. Renders a ring with a filled sector
 * that sweeps clockwise from the top as `progress` increases from 0 to 1.
 *
 * Color is inherited from the parent's text color via `currentColor` — set
 * `text-{color}` on a wrapping element to control it, the same way as `Icon`.
 *
 * Pass `aria-label` to make the indicator accessible as a `progressbar`.
 * Omit it when a parent element already provides the accessible context
 * (e.g. a button with its own label) — the SVG will be `aria-hidden` in that case.
 */
export function ProgressCircle({
  progress,
  "aria-label": ariaLabel,
  "aria-valuetext": ariaValuetext,
}: ProgressCircleProps) {
  const sectorPath = pieSector(progress)
  const valuenow = Math.round(progress * 100)

  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      className="shrink-0"
      {...(ariaLabel
        ? {
            role: "progressbar" as const,
            "aria-label": ariaLabel,
            "aria-valuenow": valuenow,
            "aria-valuemin": 0,
            "aria-valuemax": 100,
            "aria-valuetext": ariaValuetext ?? `${valuenow}% complete`,
          }
        : { "aria-hidden": "true" as const }
      )}
    >
      <circle cx={CENTER} cy={CENTER} r={RADIUS} className="fill-none stroke-current stroke-[1.5]" />
      {progress >= 1 && (
        <circle cx={CENTER} cy={CENTER} r={RADIUS} className="fill-current" />
      )}
      {sectorPath && (
        <path d={sectorPath} className="fill-current" />
      )}
    </svg>
  )
}

ProgressCircle.displayName = "ProgressCircle"
